import {useFormControl} from '@/src/shared/modules/form'
import {useEffect, useRef} from 'react'
import {useController} from 'react-hook-form'
import type {AutoCompleteProps} from './autocomplete'
import {AutoComplete} from './autocomplete'
import {BaseOptionType} from 'antd/es/select'
import first from 'lodash/first'
import castArray from 'lodash/castArray'

export interface FormAutoCompleteProps extends Omit<AutoCompleteProps, 'onEmpty' | 'onChange'> {
  name: string
  subscribeCleanup?: (cleanupFn: () => void) => void
}

const useCleanup = (
  field: ReturnType<typeof useController>['field'],
  subscribeCleanup?: (cleanupFn: () => void) => void,
) => {
  useEffect(() => {
    if (subscribeCleanup) {
      subscribeCleanup(() => field.onChange(''))
    }
  }, [subscribeCleanup])
}

export const FormAutoComplete = (props: FormAutoCompleteProps) => {
  const formControl = useFormControl()

  const {field: inputField} = useController({
    name: `${props.name}-autocomplete`,
    control: formControl.control,
  })

  const {field: originalField, fieldState: originalState} = useController({
    name: props.name,
    control: formControl.control,
  })

  useCleanup(inputField, props.subscribeCleanup)

  const onEmpty = () => {
    originalField.onChange(null)
  }

  // Marks whether the field currently shows a committed value (pre-filled or selected).
  // On the first keystroke such a value is replaced by the newly typed text instead of being
  // appended to it (rc-select starts its search input from the current value).
  const replacingValue = useRef(true)

  const onChange = (value: any, option: BaseOptionType | BaseOptionType[] | undefined) => {
    const option_ = first(castArray(option))
    const selected = option_ && option_.value != null

    if (selected) {
      const val = option_.value ?? value

      inputField.onChange(val)
      if (val) {
        originalField.onChange(val, {shouldValidate: true, shouldTouch: true})
      }

      replacingValue.current = true
    } else if (typeof value === 'string') {
      const current = inputField.value

      if (replacingValue.current && current && value.startsWith(current) && value.length > current.length) {
        inputField.onChange(value.slice(current.length))
        originalField.onChange(null)
      } else {
        inputField.onChange(value)
      }

      replacingValue.current = false
    }
  }

  return (
    <AutoComplete
      {...props}
      status={originalState.invalid ? 'error' : undefined}
      onChange={onChange}
      value={inputField.value}
      onEmpty={onEmpty}
    />
  )
}
